interface WebkitWindow {
  webkit?: { messageHandlers?: Record<string, { postMessage(body: unknown): void } | undefined> };
}

/**
 * The iOS shell's keep-awake switch — but only when the running build of the shell actually
 * registered the handler for it. Shared by the paid app and the App Clip, which are one Swift
 * shell compiled twice and register this handler outside the `#if APPCLIP` split.
 *
 * Presence is a real runtime question here, not a formality: a web deploy and an App Store release
 * are never in step, so the page can be newer than the binary it is running inside. And presence
 * decides *strategy*, not merely whether one postMessage lands — src/platform/wakeLock.ts uses
 * this hook INSTEAD of navigator.wakeLock wherever it exists. Returning a function unconditionally
 * would leave a shell built before this feature with neither: the post would go nowhere, silently,
 * and the web API (WebKit 16.4+) would never be tried.
 *
 * Read lazily rather than at module load: `window` does not exist under the node test environment,
 * and a bridge module that cannot be imported there would take initPlatform's tests with it.
 */
export function nativeKeepAwake(): ((on: boolean) => void) | undefined {
  if (typeof window === 'undefined') return undefined;
  // GameWebView registers its handlers on the configuration before the page starts loading, so by
  // the time anything asks, the answer is already final.
  const handler = (window as unknown as WebkitWindow).webkit?.messageHandlers?.keepAwake;
  if (!handler) return undefined;
  return (on: boolean) => {
    try {
      handler.postMessage({ on });
    } catch { /* the channel died with the page — the screen just dims, as it did before */ }
  };
}
